import { useEffect, useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { ApiError } from '@portal/platform/http';
import { changePassword, useSession } from '@portal/platform/session';
import { useShellTexts } from '../i18n/use-shell-texts';
import { Card, CardBody, CardHeader } from '../kit/card';
import { Alert } from './auth-card';
import { PasswordForm } from './password-form';
import { REDIRECT_DELAY_MS } from './reset-password-page';

/**
 * The backend answers a wrong current password with a 500 whose message says so (its error
 * handler has no branch for "Invalid current password"), so the message is the only signal.
 */
function isWrongCurrentPassword(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }
  const message = (error.body as { message?: unknown } | null)?.message;
  return typeof message === 'string' && /current password/i.test(message);
}

/** The logged-in user's own password, inside the shell (`/reset-password` without a token). */
export function ChangePasswordPage() {
  const t = useShellTexts().resetPassword;
  const { platform } = useSession();
  const router = useRouter();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!done) {
      return;
    }
    const timer = setTimeout(() => void router.navigate({ href: '/dashboard' }), REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [done, router]);

  async function submit({ current, next }: { current: string; next: string }) {
    setSubmitting(true);
    setError(null);
    try {
      await changePassword(platform.client, current, next);
      setDone(true);
    } catch (failure) {
      setError(isWrongCurrentPassword(failure) ? t.wrongCurrentPassword : t.failed);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <h1 className="mb-0 text-sm leading-normal font-bold">
          {done ? t.changeComplete : t.changeHeading}
        </h1>
      </CardHeader>
      <CardBody>
        {done ? (
          <Alert tone="success">{t.redirectingToDashboard}</Alert>
        ) : (
          <>
            {error ? <Alert tone="error">{error}</Alert> : null}
            <PasswordForm
              askCurrent
              submitLabel={t.changeButton}
              submittingLabel={t.changing}
              submitting={submitting}
              onSubmit={submit}
            >
              <p className="mb-4 text-ink-muted">{t.changeMessage}</p>
            </PasswordForm>
          </>
        )}
      </CardBody>
    </Card>
  );
}
